/*
 * Copyright (c) 2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import { PackagesCartItemVoucher } from 'types/entities/PackagesCartItemVoucher'
import { useAppDispatch } from 'helpers/store'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { useVoucherTypeByArticle } from 'utils/voucherUtils'
import BookingSellerReservationDetailVoucherActions from 'pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerReservationDetailVoucherActions'
import { PackagesCart } from 'types/entities/PackagesCart'
import { PlusIcon } from '@heroicons/react/24/outline'
import { closeSecondaryModal, openSecondaryModal } from 'helpers/actions'
import { ModalSize } from 'types/enums/ModalSize'
import BookingSellerNewVoucherModal from 'pages/Front/Booking/Seller/BookingSellerReservationDetail/components/BookingSellerNewVoucherModal'
import { useEffect, useMemo } from 'react'
import { createColumnHelper } from '@tanstack/react-table'
import UiTanstackTable from 'components/Ui/UiTanstackTable'
import { useTable } from 'utils/hooks/useTable'
import UiTableHeaderLabel from 'components/Ui/UiTableHeaderLabel'

interface BookingSellerPackageCartItemVouchersTableProps {
    packagesCartItemVouchers: PackagesCartItemVoucher[]
    packagesCart: PackagesCart
    title?: string
    className?: string
}

const BookingSellerPackageCartItemVouchersTable = (
    props: BookingSellerPackageCartItemVouchersTableProps
) => {
    const { t } = useTranslation()
    const { getVoucherType } = useVoucherTypeByArticle()
    const dispatch = useAppDispatch()

    const columnHelper = createColumnHelper<PackagesCartItemVoucher>()

    const columns = useMemo(
        () => [
            columnHelper.accessor('voucher.campaign_name', {
                header: () => (
                    <UiTableHeaderLabel>{t('Code')}</UiTableHeaderLabel>
                ),
                cell: (info) => (
                    <div className="text-[11px] font-medium text-gray-900 dark:text-gray-100">
                        {info.getValue()}
                    </div>
                ),
            }),
            columnHelper.display({
                id: 'type',
                header: () => (
                    <div className="flex justify-end">
                        <UiTableHeaderLabel>{t('Type')}</UiTableHeaderLabel>
                    </div>
                ),
                cell: (info) => (
                    <div className="text-left text-[11px] text-gray-700 dark:text-gray-300">
                        {getVoucherType(info.row.original.voucher).label}
                    </div>
                ),
            }),
            columnHelper.display({
                id: 'price',
                header: () => (
                    <div className="flex justify-end">
                        <UiTableHeaderLabel>{t('Price')}</UiTableHeaderLabel>
                    </div>
                ),
                cell: (info) => (
                    <div className="text-left text-[11px] text-gray-700 dark:text-gray-300">
                        -{' '}
                        {
                            getVoucherType(info.row.original.voucher)
                                .valueFormatted
                        }
                    </div>
                ),
            }),
            columnHelper.display({
                id: 'actions',
                enableSorting: false,
                enableColumnFilter: false,
                header: () => (
                    <div className="flex justify-end">
                        <UiTableHeaderLabel>{t('Actions')}</UiTableHeaderLabel>
                    </div>
                ),
                cell: (info) => (
                    <div className="flex justify-end">
                        <BookingSellerReservationDetailVoucherActions
                            packagesCartItemVoucher={info.row.original}
                            packagesCart={props.packagesCart}
                        />
                    </div>
                ),
            }),
        ],
        [t, columnHelper, getVoucherType, props.packagesCart]
    )

    const tableInit = useTable<PackagesCartItemVoucher>({
        defaultData: props.packagesCartItemVouchers,
        columns: columns,
        defaultRecordsPerPage: 999,
    })

    useEffect(() => {
        tableInit.setRecordsTable(props.packagesCartItemVouchers)
    }, [props.packagesCartItemVouchers, tableInit.setRecordsTable])

    const onClickAddVoucher = () => {
        if (!props.packagesCart) {
            return
        }
        dispatch(
            openSecondaryModal(
                <BookingSellerNewVoucherModal
                    packagesCart={props.packagesCart}
                    closeModal={() => dispatch(closeSecondaryModal())}
                />,
                {
                    modalSize: ModalSize.Small,
                },
                t('Add Voucher')
            )
        )
    }

    return (
        <div
            className={clsx(
                'BookingSellerPackageCartItemVouchersTable UiCard p-2 flex flex-col',
                props.className
            )}
        >
            <div className="flex items-center justify-between mb-2 pb-1 border-b border-gray-200 dark:border-gray-700 shrink-0">
                <h3 className="text-xs font-semibold text-gray-700 dark:text-gray-300">
                    {props.title ?? t('Vouchers')}
                </h3>
                <button
                    onClick={onClickAddVoucher}
                    className="p-1 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 rounded transition-colors"
                    title={t('Add voucher') as string}
                >
                    <PlusIcon className="w-4 h-4 text-white" />
                </button>
            </div>

            <div className="">
                {props.packagesCartItemVouchers.length > 0 ? (
                    <UiTanstackTable
                        tableInit={tableInit}
                        className="h-full"
                        hidePagination={true}
                    />
                ) : (
                    <div className="flex flex-col items-center justify-center h-full text-gray-500 text-xs py-8">
                        <span>{t('No vouchers')}</span>
                    </div>
                )}
            </div>
        </div>
    )
}

export default BookingSellerPackageCartItemVouchersTable
